import { Navigate, Route, Routes } from 'react-router-dom'
import { Layout } from '@/components/layout/Layout'
import { Dashboard } from '@/pages/Dashboard'
import { PrescriptionList } from '@/pages/prescriptions/PrescriptionList'
import { NewPrescription } from '@/pages/prescriptions/NewPrescription'
import { PrescriptionDetails } from '@/pages/prescriptions/PrescriptionDetails'
import { ExternalPrescriptionDetails } from '@/components/externalLabs/ExternalPrescriptionDetails'
import { ProfilePage } from '@/pages/profile/ProfilePage'
import CompanyList from '@/pages/companies/CompanyList'
import CompanyForm from '@/pages/companies/CompanyForm'
import OrganizationList from '@/pages/organizations/OrganizationList'
import OrganizationForm from '@/pages/organizations/OrganizationForm'
import UserList from '@/pages/users/UserList'
import UserForm from '@/pages/users/UserForm'
import ListinoList from '@/pages/listino/ListinoList'
import ListinoForm from '@/pages/listino/ListinoForm'
import PrimoUpDebug from '@/components/debug/PrimoUpDebug'
import { PrimoupChatDeepLink } from '@/pages/chat/PrimoupChatDeepLink'
import { Loading } from '@/components/ui/loading'
import { PermissionGuard } from '@/components/ui/PermissionGuard'
import { lazy, Suspense } from 'react'

// Lazy load accounting page
const AccountingList = lazy(() => import('@/pages/accounting/AccountingList').then(module => ({ default: module.AccountingList })))
const ListinoMedicoPage = lazy(() => import('@/pages/listino/ListinoMedicoPage'))
const AdminCatalogRequestsPage = lazy(() => import('@/pages/admin/AdminCatalogRequestsPage'))
const AdminContabilitaCodePage = lazy(() => import('@/pages/admin/AdminContabilitaCodePage'))
const AdminImpersonatePage = lazy(() => import('@/pages/admin/AdminImpersonatePage'))
const AdminRepairPrescriptionPage = lazy(() => import('@/pages/admin/AdminRepairPrescriptionPage'))
const AdminImplantConfigPage = lazy(() => import('@/pages/admin/AdminImplantConfigPage'))
const CommunicationSettingsPage = lazy(() => import('@/pages/admin/CommunicationSettingsPage'))
const IsoWebhookSettingsPage = lazy(() => import('@/pages/admin/IsoWebhookSettingsPage'))
const SharedFileLayout = lazy(() => import('@/pages/share/SharedFileLayout'))
const SharedFilesPage = lazy(() => import('@/pages/sharedFiles/SharedFilesPage'))
const IsoReportsPage = lazy(() => import('@/pages/iso/IsoReportsPage'))
const CustomerHealthPage = lazy(() => import('@/pages/reports/CustomerHealthPage'))
const ChatQrUploadPage = lazy(() => import('@/pages/chat/ChatQrUploadPage'))
const RolePermissionsPage = lazy(() => import('@/pages/admin/RolePermissionsPage'))
const CompanyAppearancePage = lazy(() => import('@/pages/companies/CompanyAppearancePage'))
const AnalyticsDashboardPage = lazy(() => import('@/pages/analytics/AnalyticsDashboardPage'))
const AnalyticsClientsPage = lazy(() => import('@/pages/analytics/AnalyticsClientsPage'))
const HelpPage = lazy(() => import('@/pages/help/HelpPage'))
const ComponentisticaPage = lazy(() => import('@/pages/componentistica/ComponentisticaPage'))
const ComponentisticaSettingsPage = lazy(() => import('@/pages/componentistica/ComponentisticaSettingsPage'))
const AnalyticsPreviewPage = lazy(() =>
  import('@/pages/analytics/AnalyticsDashboardPage').then((module) => ({
    default: module.AnalyticsPreviewPage,
  }))
)
const AnalyticsClientsPreviewPage = lazy(() =>
  import('@/pages/analytics/AnalyticsClientsPage').then((module) => ({
    default: module.AnalyticsClientsPreviewPage,
  }))
)


function App() {
  return (
    <Suspense fallback={<Loading text="Caricamento pagina..." />}>
      <Routes>
      {/* Rotta pubblica per link condivisi: Auth0 richiesto, NESSUN profilo app necessario.
          Deve stare FUORI dal Layout per evitare l'auto-creazione del profile di useAuth(). */}
      <Route path="/share/:token" element={<SharedFileLayout />} />
      {/* Capability QR monouso: pagina volutamente fuori dal Layout e senza login Auth0. */}
      <Route path="/chat-upload" element={<ChatQrUploadPage />} />
      {import.meta.env.DEV && <Route path="/__preview/analytics" element={<AnalyticsPreviewPage />} />}
      {import.meta.env.DEV && <Route path="/__preview/analytics/clients" element={<AnalyticsClientsPreviewPage />} />}

      <Route path="/" element={<Layout />}>
        <Route index element={
          <PermissionGuard permission="view_dashboard"><Dashboard /></PermissionGuard>
        } />
        <Route path="prescriptions" element={
          <PermissionGuard anyPermission={['view_prescriptions', 'view_external_prescriptions']}><PrescriptionList /></PermissionGuard>
        } />
        <Route path="prescriptions/new" element={
          <PermissionGuard anyPermission={['create_prescription', 'create_external_prescription']}><NewPrescription /></PermissionGuard>
        } />
        <Route path="componentistica" element={
          <PermissionGuard permission="view_componentistica"><ComponentisticaPage /></PermissionGuard>
        } />
        <Route path="admin/componentistica" element={
          <PermissionGuard permission="manage_componentistica"><ComponentisticaSettingsPage /></PermissionGuard>
        } />
        <Route path="prescriptions/external/:id" element={
          <PermissionGuard permission="view_external_prescriptions"><ExternalPrescriptionDetails /></PermissionGuard>
        } />
        <Route path="prescriptions/:id" element={
          <PermissionGuard permission="view_prescriptions"><PrescriptionDetails /></PermissionGuard>
        } />
        <Route path="chat/primoup/:externalPrescriptionId" element={
          <PermissionGuard permission="use_prescription_chat"><PrimoupChatDeepLink /></PermissionGuard>
        } />
        <Route path="iso" element={
          <PermissionGuard permission="view_iso_reports"><IsoReportsPage /></PermissionGuard>
        } />
        <Route path="reports/customer-health" element={
          <PermissionGuard permission="view_customer_health"><CustomerHealthPage /></PermissionGuard>
        } />
        
        {/* Authenticated profile and help routes */}
        <Route path="profile" element={<ProfilePage />} />
        <Route path="help" element={<HelpPage />} />
        
        {/* Accounting routes */}
        <Route path="accounting" element={
            <PermissionGuard permission="view_accounting"><AccountingList /></PermissionGuard>
        } />
        <Route path="analytics" element={
          <PermissionGuard permission="view_analytics"><AnalyticsDashboardPage /></PermissionGuard>
        } />
        <Route path="analytics/clients" element={
          <PermissionGuard permission="view_analytics">
            <AnalyticsClientsPage />
          </PermissionGuard>
        } />
        
        {/* Società e cliniche */}
        <Route path="organizations" element={
          <PermissionGuard anyPermission={['view_companies', 'manage_companies']}><OrganizationList /></PermissionGuard>
        } />
        <Route path="organizations/new" element={
          <PermissionGuard permission="manage_companies"><OrganizationForm /></PermissionGuard>
        } />
        <Route path="organizations/:id/edit" element={
          <PermissionGuard anyPermission={['view_companies', 'manage_companies']}><OrganizationForm /></PermissionGuard>
        } />
        <Route path="companies" element={
          <PermissionGuard anyPermission={['view_companies', 'manage_companies']}><CompanyList /></PermissionGuard>
        } />
        <Route path="companies/new" element={
          <PermissionGuard permission="manage_companies"><CompanyForm /></PermissionGuard>
        } />
        <Route path="companies/:id/edit" element={
          <PermissionGuard permission="manage_companies"><CompanyForm /></PermissionGuard>
        } />
        <Route path="company/appearance" element={
          <PermissionGuard permission="manage_company_branding">
            <CompanyAppearancePage />
          </PermissionGuard>
        } />
        
        {/* Users routes */}
        <Route path="users" element={
          <PermissionGuard permission="manage_users"><UserList /></PermissionGuard>
        } />
        <Route path="users/new" element={
          <PermissionGuard permission="manage_users"><UserForm /></PermissionGuard>
        } />
        <Route path="users/:id/edit" element={
          <PermissionGuard permission="manage_users"><UserForm /></PermissionGuard>
        } />
        
        {/* Listino routes */}
        <Route path="listino" element={
          <PermissionGuard anyPermission={['view_listino', 'view_external_prescriptions', 'manage_external_laboratories']}><ListinoList /></PermissionGuard>
        } />
        <Route path="listino/medico" element={
          <PermissionGuard permission="view_listino_medico"><ListinoMedicoPage /></PermissionGuard>
        } />
        <Route path="listino/new" element={
          <PermissionGuard permission="manage_listino"><ListinoForm /></PermissionGuard>
        } />
        <Route path="listino/:id/edit" element={
          <PermissionGuard anyPermission={['manage_listino', 'manage_company_phases', 'manage_phase_policies']}>
            <ListinoForm />
          </PermissionGuard>
        } />

        <Route path="admin/catalog-requests" element={
          <PermissionGuard permission="approve_catalog_listino"><AdminCatalogRequestsPage /></PermissionGuard>
        } />
        <Route path="admin/contabilita-code" element={
          <PermissionGuard permission="manage_contabilita_esclusioni"><AdminContabilitaCodePage /></PermissionGuard>
        } />
        <Route path="admin/impersonate" element={
          <PermissionGuard permission="use_impersonation"><AdminImpersonatePage /></PermissionGuard>
        } />
        <Route path="admin/role-permissions" element={
          <PermissionGuard permission="manage_role_permissions"><RolePermissionsPage /></PermissionGuard>
        } />
        <Route path="admin/repair-prescription" element={
          <PermissionGuard permission="manage_prescriptions"><AdminRepairPrescriptionPage /></PermissionGuard>
        } />
        <Route path="admin/implant-config" element={
          <PermissionGuard permission="manage_implant_config"><AdminImplantConfigPage /></PermissionGuard>
        } />
        <Route path="admin/communications" element={
          <PermissionGuard anyPermission={['manage_communication_templates', 'manage_settings']}>
            <CommunicationSettingsPage />
          </PermissionGuard>
        } />
        <Route path="admin/iso-webhook" element={
          <PermissionGuard permission="manage_iso_webhook"><IsoWebhookSettingsPage /></PermissionGuard>
        } />
        
        {/* Legacy bookmark: la gestione comunicazioni ora ha una sola pagina canonica. */}
        <Route path="settings" element={
          <PermissionGuard permission="manage_settings">
            <Navigate to="/admin/communications?tab=operational" replace />
          </PermissionGuard>
        } />

        {/* Shared files (standalone) */}
        <Route path="shared-files" element={
          <PermissionGuard permission="view_shared_files"><SharedFilesPage /></PermissionGuard>
        } />

        {/* Debug routes */}
        <Route path="debug/primoup" element={
          <PermissionGuard permission="manage_primoup_integration"><PrimoUpDebug /></PermissionGuard>
        } />
        
        <Route path="*" element={<div className="text-center py-8">Pagina non trovata</div>} />
      </Route>
      </Routes>
    </Suspense>
  )
}

export default App
